/**
 * System report: the hierarchy (group, unit, component, subcomponent) built
 * from the physical_components subtree (plus a "Site" group of the live
 * cards for the RFIMS sites, or only that group for a flat element without
 * the subtree), the status badge with its fallback, the health bar
 * maths, the lifecycle estimate, the spare parts cell, the AI prediction
 * cell with its sanity overrides, the summary counts, the flat-list filter
 * and the CSV export. Port of the data model of system-report.js; every
 * DOM write becomes a returned field. Time is injected.
 */
import type { DataItem, Element, Reading, StatusColour, Subsystem } from '../types';
import { isRfims } from '@/features/rfims/registry';
import { COMPONENT_LIFETIMES } from './constants';
import { addDaysMs } from './componentPrediction';
import type { InventoryManager, InventoryStatus } from './inventory';
import type { PredictionReport, SensorPrediction } from './predictionEngine';
import { deriveComponentStatus, withReadings } from './statusDerivation';

export type ReportItemType = 'unit' | 'component' | 'subcomponent';

/** OPERATIONAL, WARNING, DEGRADED or OFFLINE from the derivation; CRITICAL from a red status item. */
export type ReportStatus = 'OPERATIONAL' | 'WARNING' | 'DEGRADED' | 'OFFLINE' | 'CRITICAL';

/** A prediction attached to a row: the engine's shape, the AI service's shape, or a mix after the overrides. */
export interface ReportPrediction {
  /** Engine: YYYY-MM-DD. */
  predictedFailure?: string | null;
  /** AI service: YYYY-MM-DD or null. */
  predictedFailureDate?: string | null;
  daysUntilFailure: number | null;
  confidence: number;
  aiHealth?: number;
  healthScore?: number;
  trend?: { direction: string; rate?: string; anomalousReadings?: number } | null;
  recommendedAction?: string;
  leadIndicators?: unknown[];
  riskLevel?: string;
  maintenanceWindow?: string;
  inventoryAlert?: string | null;
  failureMode?: string | null;
  justification?: string;
  dependencyRisk?: string | null;
  isAIGenerated?: boolean;
  generatedAt?: string;
}

export interface ReportRow {
  id: string;
  name: string;
  parentLabel: string;
  itemType: ReportItemType;
  category: string;
  status: ReportStatus;
  statusColour: StatusColour;
  /** 0 to 100 after the status cap. */
  health: number;
  /** Positive when readings drift away from the normal midpoint. */
  healthTrend: number;
  predictedReplacementMs: number | null;
  /** Days. */
  timeToReplacement: number | null;
  replacementReason: string;
  inventory: InventoryStatus | null;
  data: DataItem[];
  aiPrediction: ReportPrediction | null;
  /** Units carry their component and subcomponent rows. */
  components?: ReportRow[];
}

export interface ReportGroup {
  type: 'group';
  id: string;
  name: string;
  units: ReportRow[];
}

export interface ReportSummary {
  total: number;
  operational: number;
  warning: number;
  critical: number;
  /** Rows whose prediction is within 30 days (a "no failure" prediction, null days, is not counted). */
  criticalPredictions: number;
  /** Rows whose prediction is within 180 days but not 30. */
  warningPredictions: number;
  /** The row with the fewest predicted days; predictions without days do not take part. */
  nearestFailure: { name: string; days: number; date: string | null } | null;
  averageConfidence: number | null;
}

export interface ReportInput {
  readings?: Record<string, Reading>;
  history?: Record<string, Reading[]>;
  /** PredictionEngine report to merge by equipment keyword. */
  predictions?: PredictionReport | null;
  /** Per-component AI predictions (take precedence over the engine's). */
  aiPredictions?: Record<string, ReportPrediction>;
  inventory?: Pick<InventoryManager, 'getInventoryForEquipment'> | null;
  nowMs: number;
}

export interface SystemReport {
  groups: ReportGroup[];
  /** Flat list in the original order: a unit's components then the unit. */
  rows: ReportRow[];
  summary: ReportSummary;
}

/** Category from COMPONENT_LIFETIMES by exact id, then the first keyword contained in it, else "Other". */
export function getComponentCategory(componentId: string): string {
  const id = componentId.toLowerCase();
  if (COMPONENT_LIFETIMES[id]) return COMPONENT_LIFETIMES[id].category;
  for (const keyword of Object.keys(COMPONENT_LIFETIMES)) {
    if (id.indexOf(keyword) !== -1) return COMPONENT_LIFETIMES[keyword].category;
  }
  return COMPONENT_LIFETIMES.default.category;
}

/**
 * Per item: red 10, yellow 50; a green item with a normalRange scores
 * 70 to 100 centred in the range, 40 to 70 within a 20 % band outside it,
 * 0 to 40 beyond; items without a range are skipped. Result = 70 % of the
 * average + 30 % of the minimum, rounded; 100 without scores.
 */
export function calculateComponentHealth(subsystem: Pick<Subsystem, 'data'>): number {
  if (!subsystem.data || subsystem.data.length === 0) return 100;
  const healthScores: number[] = [];

  for (const dataItem of subsystem.data) {
    let healthScore: number;
    if (dataItem.status) {
      const sensorStatus = String(dataItem.status).toLowerCase();
      if (sensorStatus === 'red') {
        healthScores.push(10);
        continue;
      } else if (sensorStatus === 'yellow') {
        healthScores.push(50);
        continue;
      }
    }
    if (!dataItem.normalRange) continue;

    const value = parseFloat(String(dataItem.value || '').replace(/[^0-9.\-e]/gi, ''));
    if (isNaN(value)) continue;

    const normal = dataItem.normalRange;
    const min = normal.low;
    const max = normal.high;
    if (min === undefined || max === undefined) continue;
    const range = max - min;
    if (range === 0) continue;

    const midpoint = (min + max) / 2;
    const deviation = Math.abs(value - midpoint);
    const maxDeviation = range / 2;
    if (value >= min && value <= max) {
      healthScore = 100 - (deviation / maxDeviation) * 30;
    } else {
      const outsideAmount = value < min ? min - value : value - max;
      const warningRange = range * 0.2;
      if (outsideAmount <= warningRange) healthScore = 70 - (outsideAmount / warningRange) * 30;
      else healthScore = Math.max(0, 40 - (outsideAmount / warningRange) * 20);
    }
    healthScores.push(healthScore);
  }

  if (healthScores.length === 0) return 100;
  let sum = 0;
  let minScore = 100;
  for (const score of healthScores) {
    sum += score;
    if (score < minScore) minScore = score;
  }
  const avgScore = sum / healthScores.length;
  return Math.round(avgScore * 0.7 + minScore * 0.3);
}

/**
 * Per item with at least three history points and a normalRange: the mean
 * deviation from the midpoint over the newer half of the last ten points
 * minus the older half, over half the range; averaged over items. Positive
 * means drifting away from normal.
 */
export function calculateHealthTrend(subsystem: Pick<Subsystem, 'data'>): number {
  if (!subsystem.data) return 0;
  const trends: number[] = [];

  for (const dataItem of subsystem.data) {
    if (!dataItem.history || dataItem.history.length < 3 || !dataItem.normalRange) continue;
    const min = dataItem.normalRange.low;
    const max = dataItem.normalRange.high;
    if (min === undefined || max === undefined) continue;
    const midpoint = (min + max) / 2;

    const recentHistory = dataItem.history.slice(0, Math.min(10, dataItem.history.length));
    const deviations: number[] = [];
    for (const point of recentHistory) {
      const histValue = parseFloat(String(point.value).replace(/[^0-9.\-e]/gi, ''));
      if (!isNaN(histValue)) deviations.push(Math.abs(histValue - midpoint));
    }
    if (deviations.length < 2) continue;

    let earlyAvg = 0;
    let lateAvg = 0;
    const half = Math.floor(deviations.length / 2);
    for (let k = 0; k < half; k++) lateAvg += deviations[k];
    for (let m = half; m < deviations.length; m++) earlyAvg += deviations[m];
    earlyAvg /= deviations.length - half;
    lateAvg /= half;
    trends.push((lateAvg - earlyAvg) / ((max - min) / 2));
  }

  if (trends.length === 0) return 0;
  return trends.reduce((a, b) => a + b, 0) / trends.length;
}

export interface ReplacementEstimate {
  dateMs: number;
  daysRemaining: number;
  reason: string;
}

/**
 * Base lifetime from COMPONENT_LIFETIMES (exact id, then the first keyword
 * in it, else default) × health; ÷(1 + 2·trend) when the trend is positive,
 * ×(1 − 0.5·trend) when negative; clamped 7 days to 10 years; the date is
 * today plus the rounded days (local calendar).
 */
export function calculatePredictedReplacement(
  subsystem: Pick<Subsystem, 'id'>,
  currentHealth: number,
  healthTrend: number,
  nowMs: number,
): ReplacementEstimate {
  const id = subsystem.id.toLowerCase();
  let lifetimeConfig = COMPONENT_LIFETIMES[id] || COMPONENT_LIFETIMES.default;
  if (!COMPONENT_LIFETIMES[id]) {
    for (const keyword of Object.keys(COMPONENT_LIFETIMES)) {
      if (id.indexOf(keyword) !== -1) {
        lifetimeConfig = COMPONENT_LIFETIMES[keyword];
        break;
      }
    }
  }

  const baseLifetimeDays = lifetimeConfig.baseLifetime * 365;
  const lifetimeConsumedRatio = 1 - currentHealth / 100;
  let remainingLifetimeDays = baseLifetimeDays * (1 - lifetimeConsumedRatio);
  if (healthTrend > 0) remainingLifetimeDays /= 1 + healthTrend * 2;
  else if (healthTrend < 0) remainingLifetimeDays *= 1 - healthTrend * 0.5;
  remainingLifetimeDays = Math.max(7, remainingLifetimeDays);
  remainingLifetimeDays = Math.min(3650, remainingLifetimeDays);

  let reason = 'Normal lifecycle';
  if (currentHealth < 40) reason = 'Low health detected';
  else if (healthTrend > 0.2) reason = 'Degradation trend detected';
  else if (currentHealth < 70 && healthTrend > 0) reason = 'Gradual wear observed';

  return {
    dateMs: addDaysMs(nowMs, Math.round(remainingLifetimeDays)),
    daysRemaining: Math.round(remainingLifetimeDays),
    reason,
  };
}

/** Partial-match map from a component id keyword to an inventory equipment key, tried after the direct lookup. */
const INVENTORY_KEYWORDS: ReadonlyArray<readonly [string, string]> = [
  ['cooling_fan', 'cooling_fan'],
  ['fan', 'cooling_fan'],
  ['lna', 'lna'],
  ['low_noise_amplifier', 'lna'],
  ['hpa', 'hpa'],
  ['sspa', 'hpa'],
  ['modem', 'modem'],
  ['antenna', 'antenna'],
  ['drive_system', 'antenna'],
  ['freq_translator', 'freq_translator'],
  ['frequency_translator', 'freq_translator'],
  ['waveguide', 'waveguide'],
  ['network_switch', 'network_switch'],
  ['switch_matrix', 'network_switch'],
];

export function getInventoryStatus(
  componentId: string,
  inventory: Pick<InventoryManager, 'getInventoryForEquipment'> | null | undefined,
): InventoryStatus | null {
  if (!inventory) return null;
  const direct = inventory.getInventoryForEquipment(componentId);
  if (direct) return direct;
  const id = componentId.toLowerCase();
  for (const [keyword, equipment] of INVENTORY_KEYWORDS) {
    if (id.indexOf(keyword) !== -1) return inventory.getInventoryForEquipment(equipment);
  }
  return null;
}

/**
 * One row: status from the derivation when it evaluated a sensor, else from
 * the status or overall_status item's colour (green OPERATIONAL, yellow
 * WARNING, red CRITICAL), else OPERATIONAL; health capped by status
 * (OFFLINE and CRITICAL 20, DEGRADED 50, WARNING 70).
 */
export function buildComponentInfo(
  subsystem: Subsystem,
  parentLabel: string,
  itemType: ReportItemType,
  input: Pick<ReportInput, 'inventory' | 'nowMs'>,
): ReportRow {
  const row: ReportRow = {
    id: subsystem.id,
    name: subsystem.label || subsystem.id,
    parentLabel,
    itemType,
    category: getComponentCategory(subsystem.id),
    status: 'OPERATIONAL',
    statusColour: 'green',
    health: 100,
    healthTrend: 0,
    predictedReplacementMs: null,
    timeToReplacement: null,
    replacementReason: '',
    inventory: null,
    data: subsystem.data || [],
    aiPrediction: null,
  };

  const derivation = deriveComponentStatus(subsystem);
  if (derivation.sensorStatuses.length > 0) {
    row.status = derivation.status;
    row.statusColour = derivation.colour;
  } else if (subsystem.data) {
    for (const statusItem of subsystem.data) {
      if (statusItem.id === 'status' || statusItem.id === 'overall_status') {
        if (statusItem.status) {
          row.statusColour = statusItem.status as StatusColour;
          if (statusItem.status === 'green') row.status = 'OPERATIONAL';
          else if (statusItem.status === 'yellow') row.status = 'WARNING';
          else if (statusItem.status === 'red') row.status = 'CRITICAL';
        }
        break;
      }
    }
  }

  row.health = calculateComponentHealth(subsystem);
  const upperStatus = row.status.toUpperCase();
  if (upperStatus === 'OFFLINE' || upperStatus === 'CRITICAL') row.health = Math.min(row.health, 20);
  else if (upperStatus === 'DEGRADED') row.health = Math.min(row.health, 50);
  else if (upperStatus === 'WARNING') row.health = Math.min(row.health, 70);

  row.healthTrend = calculateHealthTrend(subsystem);
  const prediction = calculatePredictedReplacement(subsystem, row.health, row.healthTrend, input.nowMs);
  row.predictedReplacementMs = prediction.dateMs;
  row.timeToReplacement = prediction.daysRemaining;
  row.replacementReason = prediction.reason;
  row.inventory = getInventoryStatus(subsystem.id, input.inventory);
  return row;
}

/** Element type of the RFIMS site elements (scripts/elements/build-rfims.mjs). */
export const RFIMS_SITE_TYPE = 'RFIMS Site';
/** The RFIMS live cards that report as units of the "Site" group (unit_info is identity, not equipment). */
export const SITE_CARD_IDS: readonly string[] = ['environmental', 'host_health', 'network'];

export interface CollectOptions {
  /**
   * The element is an RFIMS site: its top-level live cards (SITE_CARD_IDS)
   * become units of a "Site" group placed before the physical_components
   * groups. Ignored without that subtree, where the flat rule applies.
   */
  siteCards?: boolean;
}

/**
 * The hierarchy from the physical_components subtree: its children are
 * groups, their children units, and every descendant of a unit is a
 * component (two levels: component, then subcomponent). An RFIMS site
 * (options.siteCards) also gets a synthetic "Site" group, first, with its
 * live cards as units. Without the subtree (a flat element, as the RFIMS
 * sites were before they carried the antenna) every top-level subsystem with
 * data, except unit_info, is a unit of the "Site" group.
 */
export function collectComponentData(
  subsystems: Subsystem[],
  input: Pick<ReportInput, 'inventory' | 'nowMs'>,
  options: CollectOptions = {},
): { rows: ReportRow[]; groups: ReportGroup[] } {
  const rows: ReportRow[] = [];
  const groups: ReportGroup[] = [];
  const physicalComponents = subsystems.find((s) => s.id === 'physical_components');

  if (!physicalComponents) {
    const flatUnits = subsystems.filter((s) => s.id !== 'unit_info' && s.data && s.data.length > 0);
    if (flatUnits.length === 0) return { rows, groups };
    const siteGroup: ReportGroup = { type: 'group', id: 'site', name: 'Site', units: [] };
    for (const flatSub of flatUnits) {
      const flatUnit = buildComponentInfo(flatSub, siteGroup.name, 'unit', input);
      flatUnit.components = [];
      siteGroup.units.push(flatUnit);
      rows.push(flatUnit);
    }
    groups.push(siteGroup);
    return { rows, groups };
  }

  if (options.siteCards) {
    const siteGroup: ReportGroup = { type: 'group', id: 'site', name: 'Site', units: [] };
    for (const card of subsystems.filter(
      (s) => SITE_CARD_IDS.includes(s.id) && s.data && s.data.length > 0,
    )) {
      const unit = buildComponentInfo(card, siteGroup.name, 'unit', input);
      unit.components = [];
      siteGroup.units.push(unit);
      rows.push(unit);
    }
    if (siteGroup.units.length > 0) groups.push(siteGroup);
  }

  for (const group of physicalComponents.childs || []) {
    const groupData: ReportGroup = { type: 'group', id: group.id, name: group.label || group.id, units: [] };
    for (const unit of group.childs || []) {
      const unitInfo = buildComponentInfo(unit, groupData.name, 'unit', input);
      unitInfo.components = [];
      for (const childComp of unit.childs || []) {
        const compInfo = buildComponentInfo(childComp, unitInfo.name, 'component', input);
        unitInfo.components.push(compInfo);
        rows.push(compInfo);
        for (const subComp of childComp.childs || []) {
          const subInfo = buildComponentInfo(subComp, compInfo.name, 'subcomponent', input);
          unitInfo.components.push(subInfo);
          rows.push(subInfo);
        }
      }
      groupData.units.push(unitInfo);
      rows.push(unitInfo);
    }
    groups.push(groupData);
  }
  return { rows, groups };
}

function predictionKey(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '_');
}

/**
 * Attaches an engine prediction to each row: by the row id against the
 * equipment key or sensorId map, then by name keyword (cooling_fan, fan,
 * lna, hpa, antenna, modem, network) against `<keyword>_`, then by the first
 * word of the equipment key either way.
 */
export function mergePredictionsWithComponents(
  rows: ReportRow[],
  report: PredictionReport | null | undefined,
): void {
  if (!report || !report.predictions) return;
  const predictionMap: Record<string, SensorPrediction | SensorPrediction[]> = {};
  for (const pred of report.predictions) {
    const equipKey = predictionKey(pred.equipment);
    const existing = predictionMap[equipKey];
    if (!existing) predictionMap[equipKey] = [pred];
    else if (Array.isArray(existing)) existing.push(pred);
    else predictionMap[equipKey] = [pred];
    if (pred.sensorId) predictionMap[pred.sensorId] = pred;
  }
  const first = (entry: SensorPrediction | SensorPrediction[]): SensorPrediction =>
    Array.isArray(entry) ? entry[0] : entry;

  for (const comp of rows) {
    const compKey = predictionKey(comp.id);
    const compName = predictionKey(comp.name);
    let matchedPred: SensorPrediction | null = null;

    if (predictionMap[compKey]) matchedPred = first(predictionMap[compKey]);

    if (!matchedPred) {
      for (const kw of ['cooling_fan', 'fan', 'lna', 'hpa', 'antenna', 'modem', 'network']) {
        if ((compKey.indexOf(kw) !== -1 || compName.indexOf(kw) !== -1) && predictionMap[kw + '_']) {
          matchedPred = first(predictionMap[kw + '_']);
          break;
        }
      }
    }

    if (!matchedPred) {
      for (const pred of report.predictions) {
        if (matchedPred) break;
        const predEquip = predictionKey(pred.equipment);
        if (
          compKey.indexOf(predEquip.split('_')[0]) !== -1 ||
          predEquip.indexOf(compKey.split('_')[0]) !== -1
        ) {
          matchedPred = pred;
        }
      }
    }

    if (matchedPred) {
      comp.aiPrediction = {
        predictedFailure: matchedPred.predictedFailure,
        daysUntilFailure: matchedPred.daysUntilFailure,
        confidence: matchedPred.confidence,
        aiHealth: matchedPred.currentHealth,
        trend: matchedPred.trend,
        recommendedAction: matchedPred.recommendedAction,
        leadIndicators: matchedPred.leadIndicators,
      };
    }
  }
}

/** The header counts and the prediction strip numbers. */
export function summariseReport(rows: ReportRow[], report?: PredictionReport | null): ReportSummary {
  let operational = 0;
  let warning = 0;
  let critical = 0;
  let criticalPredictions = 0;
  let warningPredictions = 0;
  let nearestFailure: ReportSummary['nearestFailure'] = null;

  for (const comp of rows) {
    const status = comp.status.toUpperCase();
    if (status === 'OPERATIONAL' || status === 'ONLINE') operational++;
    else if (status === 'WARNING' || status === 'DEGRADED') warning++;
    else if (status === 'CRITICAL' || status === 'OFFLINE') critical++;
    else if (comp.statusColour === 'green') operational++;
    else if (comp.statusColour === 'yellow') warning++;
    else if (comp.statusColour === 'red') critical++;
    else operational++;

    // A "no failure" prediction (null days) takes no part (defect fix: the original compared null as 0 days).
    const days = comp.aiPrediction ? comp.aiPrediction.daysUntilFailure : null;
    if (comp.aiPrediction && days !== null && days !== undefined) {
      if (days <= 30) criticalPredictions++;
      else if (days <= 180) warningPredictions++;
      if (!nearestFailure || days < nearestFailure.days) {
        nearestFailure = {
          name: comp.name,
          days,
          date: comp.aiPrediction.predictedFailureDate ?? comp.aiPrediction.predictedFailure ?? null,
        };
      }
    }
  }

  return {
    total: rows.length,
    operational,
    warning,
    critical,
    criticalPredictions,
    warningPredictions,
    nearestFailure,
    averageConfidence: report && report.summary ? report.summary.averageConfidence || 0 : null,
  };
}

export type StatusFilter = 'all' | 'critical' | 'warning' | 'operational';

/** Any active filter switches the table to this flat list ("Parent » Name"). */
export function filterRows(
  rows: ReportRow[],
  filters: { search?: string; status?: StatusFilter; category?: string },
): ReportRow[] {
  const searchFilter = (filters.search || '').toLowerCase();
  const statusFilter = filters.status || 'all';
  const categoryFilter = filters.category || 'all';
  return rows.filter((comp) => {
    if (searchFilter) {
      const nameMatch = comp.name.toLowerCase().indexOf(searchFilter) !== -1;
      const parentMatch =
        Boolean(comp.parentLabel) && comp.parentLabel.toLowerCase().indexOf(searchFilter) !== -1;
      if (!nameMatch && !parentMatch) return false;
    }
    if (statusFilter !== 'all') {
      const status = comp.status.toUpperCase();
      const color = comp.statusColour;
      if (statusFilter === 'critical' && status !== 'CRITICAL' && status !== 'OFFLINE' && color !== 'red')
        return false;
      if (statusFilter === 'warning' && status !== 'WARNING' && status !== 'DEGRADED' && color !== 'yellow')
        return false;
      if (
        statusFilter === 'operational' &&
        status !== 'OPERATIONAL' &&
        status !== 'ONLINE' &&
        color !== 'green'
      )
        return false;
    }
    if (categoryFilter !== 'all' && comp.category !== categoryFilter) return false;
    return true;
  });
}

export function filtersActive(filters: {
  search?: string;
  status?: StatusFilter;
  category?: string;
}): boolean {
  return (
    Boolean(filters.search) || (filters.status || 'all') !== 'all' || (filters.category || 'all') !== 'all'
  );
}

export type StatusBadge = 'critical' | 'degraded' | 'warning' | 'operational';

/** Badge class: OFFLINE, CRITICAL or red is critical; DEGRADED or yellow is degraded (so WARNING only shows as warning with a non-yellow colour); else operational. */
export function getStatusBadgeClass(status: string, colour: string): StatusBadge {
  const s = status.toUpperCase();
  if (s === 'OFFLINE' || s === 'CRITICAL' || colour === 'red') return 'critical';
  if (s === 'DEGRADED' || colour === 'yellow') return 'degraded';
  if (s === 'WARNING' || colour === 'yellow') return 'warning';
  return 'operational';
}

/** The health the bar shows: the prediction's aiHealth when truthy, else the row's. */
export function displayHealth(row: Pick<ReportRow, 'health' | 'aiPrediction'>): number {
  return row.aiPrediction ? (row.aiPrediction.aiHealth ?? row.health) : row.health;
}

export function healthBarClass(health: number): 'health-good' | 'health-warning' | 'health-critical' {
  return health >= 70 ? 'health-good' : health >= 40 ? 'health-warning' : 'health-critical';
}

export interface LifecycleCell {
  /** "N yrs" above a year, else "N days". */
  label: string;
  /** Green above a year, amber above 180 days, else red. */
  colour: '#4CAF50' | '#FFC107' | '#F44336';
  /** "Oct 2027". */
  dateText: string;
  reason: string;
}

export function lifecycleCell(
  row: Pick<ReportRow, 'timeToReplacement' | 'predictedReplacementMs' | 'replacementReason'>,
): LifecycleCell | null {
  if (!row.timeToReplacement && !row.predictedReplacementMs) return null;
  const days = row.timeToReplacement as number;
  let dateText = '';
  if (row.predictedReplacementMs) {
    const d = new Date(row.predictedReplacementMs);
    if (!isNaN(d.getTime())) dateText = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
  return {
    label: days > 365 ? Math.round(days / 365) + ' yrs' : days + ' days',
    colour: days > 365 ? '#4CAF50' : days > 180 ? '#FFC107' : '#F44336',
    dateText,
    reason: row.replacementReason,
  };
}

export interface InventoryCell {
  quantity: number;
  label: 'Out of Stock' | 'Low Stock' | 'In Stock';
  statusClass: 'out-of-stock' | 'low-stock' | 'in-stock';
}

/** Quantity 0 out of stock; at or below minStock (1 when unset) low; else in stock. Null means "N/A". */
export function inventoryCell(
  inventory: Pick<InventoryStatus, 'quantity' | 'minStock'> | null,
): InventoryCell | null {
  if (!inventory) return null;
  if (inventory.quantity === 0) return { quantity: 0, label: 'Out of Stock', statusClass: 'out-of-stock' };
  if (inventory.quantity <= (inventory.minStock || 1)) {
    return { quantity: inventory.quantity, label: 'Low Stock', statusClass: 'low-stock' };
  }
  return { quantity: inventory.quantity, label: 'In Stock', statusClass: 'in-stock' };
}

function isoDate(ms: number): string {
  return new Date(ms).toISOString().split('T')[0];
}

/**
 * The cell's sanity overrides against the row health, applied in place:
 * health ≤20 CRITICAL within 7 or 30 days; ≤40 at least HIGH within 60
 * days; ≤60 lifts LOW to MEDIUM; ≥90 cancels a failure within 30 days;
 * ≥80 cancels one within 60 days.
 */
export function applyReportSanityOverrides(
  pred: ReportPrediction,
  componentHealth: number,
  componentName: string,
  nowMs: number,
): void {
  const isRoutine = (action: string | undefined): boolean =>
    Boolean(action) &&
    ((action as string).indexOf('routine monitoring') !== -1 ||
      (action as string).indexOf('No immediate action') !== -1);

  if (componentHealth <= 20) {
    pred.riskLevel = 'CRITICAL';
    if (!pred.daysUntilFailure || pred.daysUntilFailure > 30) {
      pred.daysUntilFailure = componentHealth <= 10 ? 7 : 30;
      pred.predictedFailureDate = isoDate(addDaysMs(nowMs, pred.daysUntilFailure));
    }
    pred.recommendedAction =
      'IMMEDIATE ACTION REQUIRED. Component is critically degraded. Replace or repair immediately.';
    pred.maintenanceWindow = 'Immediate';
    if (!pred.inventoryAlert)
      pred.inventoryAlert = 'Verify spare parts availability for emergency replacement.';
  } else if (componentHealth <= 40) {
    if (pred.riskLevel === 'LOW' || pred.riskLevel === 'MEDIUM') pred.riskLevel = 'HIGH';
    if (!pred.daysUntilFailure || pred.daysUntilFailure > 90) {
      pred.daysUntilFailure = 60;
      pred.predictedFailureDate = isoDate(addDaysMs(nowMs, pred.daysUntilFailure));
    }
    if (isRoutine(pred.recommendedAction)) {
      pred.recommendedAction =
        'Schedule replacement within 30-60 days. Monitor closely for further degradation.';
    }
    pred.maintenanceWindow = 'Within 30 days';
  } else if (componentHealth <= 60 && pred.riskLevel === 'LOW') {
    pred.riskLevel = 'MEDIUM';
    if (isRoutine(pred.recommendedAction))
      pred.recommendedAction = 'Schedule inspection. Component showing degradation.';
    pred.maintenanceWindow = 'Within 2-3 months';
  } else if (componentHealth >= 90 && pred.daysUntilFailure !== null && pred.daysUntilFailure <= 30) {
    pred.daysUntilFailure = null;
    pred.predictedFailureDate = null;
    pred.riskLevel = 'LOW';
    pred.justification =
      componentName +
      ' is at ' +
      componentHealth +
      '% health with all readings normal. No failure predicted within 2 years.';
    pred.recommendedAction = 'Continue routine monitoring. No immediate action required.';
    pred.maintenanceWindow = 'Standard schedule';
    pred.failureMode = null;
    pred.inventoryAlert = null;
    pred.leadIndicators = [];
  } else if (componentHealth >= 80 && pred.daysUntilFailure !== null && pred.daysUntilFailure <= 60) {
    pred.daysUntilFailure = null;
    pred.predictedFailureDate = null;
    pred.riskLevel = 'LOW';
    pred.recommendedAction = 'Continue monitoring. Component is healthy.';
    pred.maintenanceWindow = 'Standard schedule';
    pred.inventoryAlert = null;
    pred.leadIndicators = [];
  }
}

export type PredictionUrgency = 'urgent' | 'soon' | 'warning' | 'ok';

export interface AiPredictionCell {
  state: 'pending' | 'analyzing' | 'no-failure' | 'prediction';
  urgency: PredictionUrgency | null;
  days: number | null;
  /** "Mon D, YYYY" for a prediction, "Within 2 years" otherwise. */
  dateText: string;
  confidence: number | null;
  inventoryWarning: { text: string; severity: 'alert' | 'critical' | 'warning' } | null;
  trend: 'bad' | 'good' | 'stable' | null;
  isAIGenerated: boolean;
}

/** "Jan 5, 2027". */
export function formatReportDate(ms: number): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const date = new Date(ms);
  return months[date.getMonth()] + ' ' + date.getDate() + ', ' + date.getFullYear();
}

/**
 * The AI Prediction column: a spinner while pending, "No failure / Within 2
 * years" for a null days value, otherwise the days (urgent ≤30, soon ≤90,
 * warning ≤180, else ok), the date, the confidence, an inventory warning and
 * the trend arrow. Applies applyReportSanityOverrides to the row's prediction first.
 */
export function aiPredictionCell(row: ReportRow, nowMs: number, pending = false): AiPredictionCell {
  const empty = (state: AiPredictionCell['state']): AiPredictionCell => ({
    state,
    urgency: null,
    days: null,
    dateText: '',
    confidence: null,
    inventoryWarning: null,
    trend: null,
    isAIGenerated: false,
  });
  if (pending) return empty('pending');
  if (!row.aiPrediction) return empty('analyzing');

  const pred = row.aiPrediction;
  applyReportSanityOverrides(pred, row.health || 100, row.name, nowMs);

  if (pred.daysUntilFailure === null || pred.daysUntilFailure === undefined) {
    return {
      state: 'no-failure',
      urgency: 'ok',
      days: null,
      dateText: 'Within 2 years',
      confidence: pred.confidence,
      inventoryWarning: null,
      trend: null,
      isAIGenerated: Boolean(pred.isAIGenerated),
    };
  }

  const days = pred.daysUntilFailure;
  const urgency: PredictionUrgency =
    days <= 30 ? 'urgent' : days <= 90 ? 'soon' : days <= 180 ? 'warning' : 'ok';

  let inventoryWarning: AiPredictionCell['inventoryWarning'] = null;
  if (pred.inventoryAlert) inventoryWarning = { text: pred.inventoryAlert, severity: 'alert' };
  else if (row.inventory && row.inventory.quantity === 0 && days <= 90) {
    inventoryWarning = { text: 'No spare parts!', severity: 'critical' };
  } else if (row.inventory && row.inventory.quantity <= (row.inventory.minStock || 1) && days <= 180) {
    inventoryWarning = { text: 'Low stock', severity: 'warning' };
  }

  const predDate = new Date((pred.predictedFailureDate || pred.predictedFailure) as string);
  let trend: AiPredictionCell['trend'] = null;
  if (pred.trend) {
    if (pred.trend.direction === 'rising' || pred.trend.direction === 'degrading') trend = 'bad';
    else if (pred.trend.direction === 'falling') trend = 'good';
    else trend = 'stable';
  }

  return {
    state: 'prediction',
    urgency,
    days,
    dateText: formatReportDate(predDate.getTime()),
    confidence: pred.confidence,
    inventoryWarning,
    trend,
    isAIGenerated: Boolean(pred.isAIGenerated),
  };
}

/**
 * The 12-column export, one line per row in list order. The AI date column
 * takes the AI service's predictedFailureDate or the engine's
 * predictedFailure; a prediction without a date or days exports empty
 * fields (defect fix: the original read predictedFailure only and printed
 * "undefined" for an AI prediction).
 */
export function toCsv(rows: ReportRow[]): string {
  let csv =
    'Component,Parent,Type,Category,Status,Health %,AI Health %,AI Predicted Failure,AI Days Until Failure,AI Confidence %,Spare Parts,Stock Status\n';
  for (const comp of rows) {
    const inventoryQty = comp.inventory ? comp.inventory.quantity : 'N/A';
    const inventoryStatus = comp.inventory
      ? comp.inventory.quantity === 0
        ? 'Out of Stock'
        : comp.inventory.quantity <= (comp.inventory.minStock || 1)
          ? 'Low Stock'
          : 'In Stock'
      : 'N/A';
    const aiHealth = comp.aiPrediction ? (comp.aiPrediction.aiHealth ?? comp.health) : '--';
    const aiPredictedDate = comp.aiPrediction
      ? (comp.aiPrediction.predictedFailureDate ?? comp.aiPrediction.predictedFailure ?? '')
      : '--';
    const aiDaysUntil = comp.aiPrediction ? (comp.aiPrediction.daysUntilFailure ?? '') : '--';
    const aiConfidence = comp.aiPrediction ? comp.aiPrediction.confidence : '--';

    const quote = (value: unknown) => '"' + String(value).replaceAll('"', '""') + '"';
    csv += quote(comp.name) + ',';
    csv += quote(comp.parentLabel || '') + ',';
    csv += quote(comp.itemType || 'unit') + ',';
    csv += quote(comp.category) + ',';
    csv += quote(comp.status) + ',';
    csv += comp.health + ',';
    csv += aiHealth + ',';
    csv += quote(aiPredictedDate) + ',';
    csv += aiDaysUntil + ',';
    csv += aiConfidence + ',';
    csv += inventoryQty + ',';
    csv += quote(inventoryStatus) + '\n';
  }
  return csv;
}

/** system_health_report_YYYY-MM-DD.csv (UTC date). */
export function csvFileName(nowMs: number): string {
  return 'system_health_report_' + isoDate(nowMs) + '.csv';
}

/** The whole report for an element under the given readings, predictions and inventory. */
export function buildReportRows(element: Element, input: ReportInput): SystemReport {
  if (isRfims(element.id)) return { groups: [], rows: [], summary: summariseReport([]) };
  const subsystems = withReadings(element.subsystems, input.readings, input.history);
  const { rows, groups } = collectComponentData(subsystems, input, {
    siteCards: element.type === RFIMS_SITE_TYPE,
  });
  mergePredictionsWithComponents(rows, input.predictions);
  if (input.aiPredictions) {
    for (const row of rows) {
      const ai = input.aiPredictions[row.id];
      if (ai) row.aiPrediction = ai;
    }
  }
  return { groups, rows, summary: summariseReport(rows, input.predictions) };
}
